import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubRoutes, useExactRedirect, usePermissionTabs } from 'hooks';
import { globalPropTypes } from 'helpers';
import CisTabs from 'components/CisTabs/CisTabs';
import CisHeader from 'components/CisHeader/CisHeader';
import { TAB_ACTION_PORTAL_ID } from './TabActionPortal';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ipma.reports',
    url: '/ipma/reports',
  },
];

const initialTabs = [
  {
    title: 'navigation.tabs.ipma.reports.ma',
    path: '/ipma/reports/ma',
    key: 'ma',
  },
  {
    title: 'navigation.tabs.ipma.reports.yc',
    path: '/ipma/reports/yc',
    key: 'yc',
  },
];

/**
 * The reports the IPMA office reviews, one tab per kind.
 *
 * The Member Association reports were the whole section until the Young Crews
 * started reporting too; both are read the same way, so they sit side by side
 * under one heading rather than in two places in the navigation.
 *
 * Which tabs show depends on the permissions of the reader: somebody who may
 * only see the Young Crew reports lands straight on them.
 */
const Reports = ({ routes, userPermissions }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [renderSubRoutes] = useSubRoutes(routes);
  const [tabs, firstTabPath] = usePermissionTabs(initialTabs, routes, userPermissions);
  const [renderExactRedirect] = useExactRedirect('/ipma/reports', firstTabPath);

  // The heading names the tab that is open rather than the section, so a reader
  // on the Young Crew reports is not told they are looking at the MA ones.
  const activeTab = tabs.find((tab) => location.pathname.startsWith(tab.path));

  return (
    <>
      <CisHeader
        breadcrumbs={breadcrumbs}
        title={activeTab ? t(activeTab.title) : t('page.ipma.reports.name')}
        renderActions={() => <div id={TAB_ACTION_PORTAL_ID} />}
      />
      <CisTabs disableCounter items={tabs} />
      {renderExactRedirect()}
      {renderSubRoutes()}
    </>
  );
};

Reports.propTypes = {
  routes: PropTypes.arrayOf(globalPropTypes.RoutePropTypes),
  userPermissions: globalPropTypes.UserPermissions.isRequired,
};

Reports.defaultProps = {
  routes: [],
};

const mapStateToProps = ({ user }) => ({
  userPermissions: user.userPermissions,
});

export default connect(mapStateToProps)(Reports);
